import React, { useState } from 'react'
import styled from 'styled-components'
import MaskedInput from 'react-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

// Const
import { textInputStylesConfig, textInputStyles } from '../../../_constants/styles'

// Components
import { Button } from '../../../_components/Button'
import { NoteEditor, S_NoteEditor } from '../../../_components/Notes/NoteEditor'
import { ChargebackConfirmModal } from './ChargebackConfirmModal'

// Utils
import { formatCurrency, currencyToNum } from '../../../_helpers/formatting'
import { logError } from '../../../_helpers/errors'

// Services
import { performInvoiceChargeback, createInvoiceChargeback } from '../../../_services/lender.service'

//helpers
import { isFeatureEnabled } from '../../../_helpers/flags'
import { currentDateTimeInUTC, UTCDateToSystemString } from '../../../_helpers/dates'

// Hooks
import { useModal } from '../../../_reduxHooks/modal.hook'
import { S_Subtitle } from '../../../_components/Typography/Titles.styles'
import colors from '../../../_constants/colors'
import { useFlags } from '../../../_reduxHooks/flags.hook'

const numberMask = createNumberMask({
  allowDecimal: true,
})

const chargebackInputWidth = '240px'

const S_Chargeback = styled.div`
  flex: 1 1 0px;
  flex-direction: column;

  input {
    ${textInputStyles}
    width: ${chargebackInputWidth};
    max-width: ${chargebackInputWidth}; /* prevent text-area overflow*/
    min-width: ${chargebackInputWidth};
  }

  .stat {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    margin-top: 3px;

    &.has-input {
      margin-bottom: 20px;

      div:last-child {
        padding-left: 30px;
      }
    }

    div {
      min-height: ${textInputStylesConfig.minHeight};
      display: flex;
      align-items: center;
      color: ${colors.TEXT_DARK};
    }
  }

  .buttons {
    padding: 10px;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    .refund-button {
      background-color: red;
      opacity: 0.75;
      &:hover {
        opacity: 1;
      }
      &:disabled {
        opacity: 0.25;
      }
    }
  }

  ${S_NoteEditor} {
    margin-bottom: 20px;

    ${S_Subtitle} {
      font-weight: 400;
      font-size: 16px;
      margin-top: 5px;
    }
  }
`

const performChargeback = async (
  invoice,
  chargeBackAmt,
  chargeBackReason,
  closeModal,
  chargebackSuccessNotify,
  chargebackErrorNotify
) => {
  try {
    let res = await performInvoiceChargeback(invoice, chargeBackAmt, chargeBackReason)
    await performInvoiceChargeback(invoice, chargeBackAmt, chargeBackReason, res.summaries)
    closeModal()
    chargebackSuccessNotify()
  } catch (e) {
    chargebackErrorNotify()
    logError(e)
  }
}

const ChargebackModal = ({
  closeModal,
  invoice,
  onCancel,
  chargebackSuccessNotify,
  chargebackErrorNotify,
  refreshData,
}) => {
  const { openModal: openConfirmModal } = useModal()
  const [chargeBackAmt, setChargeBackAmt] = useState(invoice.balance)
  const [chargeBackReason, setChargeBackReason] = useState('')
  const { featureFlags } = useFlags()

  const defaultDate = UTCDateToSystemString(currentDateTimeInUTC()).split('T')[0]
  const [chargebackDate, setChargebackDate] = useState(defaultDate)
  const chargeback_backdate_enabled = isFeatureEnabled('chargeback_backdate', featureFlags)

  const onPerformChargeback = !isFeatureEnabled('transactions_v2', featureFlags)
    ? performChargeback
    : async (invoice, chargeBackAmt, chargeBackReason, chargebackDate) => {
        try {
          await createInvoiceChargeback(invoice.id, chargeBackAmt, chargeBackReason, chargebackDate)
          closeModal()
          chargebackSuccessNotify()
        } catch (e) {
          chargebackErrorNotify(e)
          logError(e)
          closeModal()
        }
      }

  return (
    <S_Chargeback>
      {/* Title */}
      <S_Subtitle align={'center'} marginBottom={'20'} weight={'700'}>
        Process Chargeback
      </S_Subtitle>

      {/* Stats */}
      <div className={'stat_group'}>
        <div className={'stat'}>
          <div>Invoice Number</div>
          <div>{invoice.invoice_number}</div>
        </div>

        <div className={'stat'}>
          <div>Face Value</div>
          <div>{formatCurrency(invoice.face_value)}</div>
        </div>

        <div className={'stat'}>
          <div>Balance</div>
          <div>{formatCurrency(invoice.balance)}</div>
        </div>

        <div className={'stat has-input'}>
          <div>Chargeback Amount</div>
          <div>
            {
              <MaskedInput
                inputMode="numeric"
                mask={numberMask}
                onChange={(e) => {
                  setChargeBackAmt(currencyToNum(e.target.value))
                }}
                value={chargeBackAmt}
              />
            }
          </div>
        </div>

        {chargeback_backdate_enabled && (
          <div className={'stat has-input'}>
            <div>Chargeback Date</div>
            <div>
              <input
                defaultValue={defaultDate}
                max={defaultDate}
                min={invoice.purchase_date}
                onChange={(e) => {
                  setChargebackDate(e.target.value)
                }}
                placeholder="Chargeback Date"
                type="date"
              />
            </div>
          </div>
        )}

        <NoteEditor
          flexType={'row'}
          label={'Chargeback Reason'}
          onChange={(value) => setChargeBackReason(value)}
          value={chargeBackReason}
        />
      </div>

      <div className={`buttons`}>
        <Button onClick={onCancel} text={'Cancel'} />
        <Button
          className={`refund-button`}
          disabled={!chargeBackAmt || chargeBackAmt > invoice.balance}
          onClick={() => {
            openConfirmModal(ChargebackConfirmModal, {
              onConfirmChargeback: () => {
                onPerformChargeback(
                  invoice,
                  chargeBackAmt,
                  chargeBackReason,
                  chargebackDate,
                  closeModal,
                  chargebackSuccessNotify,
                  chargebackErrorNotify
                ).then(() => refreshData())
              },
              cancelConfirm: () => {
                closeModal()
              },
            })
          }}
          text={'Confirm'}
        />
      </div>
    </S_Chargeback>
  )
}

export { ChargebackModal }
